import { Tag, message } from "antd";
import { SlotInfo } from "react-big-calendar";
import { useEffect, useState } from "react";
import {
  EventParticipantRole,
  MyCalendarEvent,
  MAX_PAGE_SIZE,
} from "../repository/data.default";
import { getCalendarEvent, getMyCalendarEvents } from "../repository/data.repository";
import dayjs from "dayjs";
import { getProblemMessage, retry } from "../utils/http";
import { QueryCondition, QueryOperation } from "../code/search-query/search-query";
import { dateRangeValue } from "../code/search-query/query-mapper";
import UserCalendarEventDialog from "./MyCalendarEventDialog";
import { CalendarEventItem, originLabel } from "./CalendarView.default";
import { MyCalendarEventForm } from "./MyCalendarEventDialog.default";
import PlatformCalendar from "./calendar/PlatformCalendar";
import useCalendarEvents, { CalendarRange } from "../hooks/useCalendarEvents";
import _ from "lodash";

const AGENDA_DESCRIPTION_LENGTH = 60;

type MyEvent = MyCalendarEvent & {
  readonly __name?: string;
  readonly __type?: string;
};

export interface MyCalendarViewProps {
  readonly openEvent?: "add" | "update" | "delete" | "preview" | null;
  readonly onEventClose?: () => void;
}

function MyCalendarView(props: MyCalendarViewProps) {
  const [eventOpen, setEventOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventItem<MyEvent>>(null!);
  const [selectedItem, setSelectedItem] = useState<MyCalendarEventForm | null>(null);
  const [eventType, setEventType] = useState<"add" | "update" | "delete" | "preview">("add");

  // The personal calendar is the signed-in user's own invitations (server-scoped), windowed to what's visible.
  const calendar = useCalendarEvents<MyEvent>({
    enabled: true,
    load: (range) => loadEvents(range),
  });

  useEffect(() => {
    if (props.openEvent) {
      setEventOpen(true);
      setEventType(props.openEvent);
      setSelectedItem({
        startDateTime: dayjs(),
        endDateTime: dayjs().add(1, "hour"),
      } as any);
    }
  }, [props.openEvent]);

  // Methods

  function loadEvents(range: CalendarRange): Promise<CalendarEventItem<MyEvent>[]> {
    // PD-01 §9 — admin's My Calendar scope: events the signed-in admin is
    // ADMINISTERING (role=Host), not every invitation they also receive as
    // an attendee. The backend feed is the same MyInvitations endpoint; we
    // tighten with a role=Host condition so attendee-only invites don't
    // clutter the operator's calendar.
    const conditions: QueryCondition[] = [
      {
        field: "eventStartDate",
        operation: QueryOperation.Between,
        values: dateRangeValue([dayjs(range.start), dayjs(range.end)]),
      },
      {
        field: "role",
        operation: QueryOperation.Equal,
        values: [EventParticipantRole.Host],
      },
    ];

    return retry(() =>
      getMyCalendarEvents({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    ).then((result) =>
      result.data.map((dataItem) => ({
        title: dataItem.eventTitle,
        start: dayjs(dataItem.eventStartDate).toDate(),
        end: dayjs(dataItem.eventEndDate).toDate(),
        dataItem,
      })),
    );
  }

  // Events

  function onEventSelect(e: CalendarEventItem<MyEvent>): void {
    setEventOpen(true);
    setEventType("preview");
    setSelectedEvent(e);
    setSelectedItem({
      title: e.title,
      description: e.dataItem!.eventDescription,
      startDateTime: dayjs(e.start),
      endDateTime: dayjs(e.end),
      company: { display: e.dataItem!.eventCompanyName, value: e.dataItem!.eventCompanyUid },
      departments: (e.dataItem!.eventDepartments ?? []).map((d) => ({
        display: d.departmentName,
        value: d.departmentUid,
      })),
      type: { display: e.dataItem!.eventTypeName, value: e.dataItem!.eventTypeUid },
      // The signed-in user's own role for this event gates management (Host → edit/delete; Attendee → respond).
      role: e.dataItem!.role,
      eventUid: e.dataItem!.eventUid,
      response: e.dataItem!.response,
      originType: e.dataItem!.eventOriginType,
    });

    // The feed row may not carry the event's departments — load them from the event itself so the
    // preview's Departments table shows what the event targets.
    const eventUid = e.dataItem!.eventUid;
    if (!e.dataItem!.eventDepartments?.length && eventUid) {
      getCalendarEvent(eventUid)
        .then((event) => {
          if (!event?.departments?.length) return;
          setSelectedItem((prev) =>
            prev?.eventUid === eventUid
              ? {
                  ...prev,
                  departments: event.departments.map((d) => ({
                    display: d.departmentName,
                    value: d.departmentUid,
                  })),
                }
              : prev,
          );
        })
        .catch(() => {});
    }
  }

  function onEventAdd(e: SlotInfo): void {
    setEventOpen(true);
    setEventType("add");
    setSelectedItem({
      startDateTime: dayjs(e.start),
      endDateTime: dayjs(e.end),
    } as any);
  }

  // The personal feed row carries the invitation's uid/rowVersion, not the event's. Edit/Delete act on
  // the event, so fetch it to obtain the correct uid + rowVersion (only hosts reach this).
  function onEventMode(mode: "update" | "delete"): void {
    const eventUid = selectedEvent.dataItem!.eventUid;
    getCalendarEvent(eventUid)
      .then((event) => {
        if (!event) return;
        setEventType(mode);
        setSelectedItem({
          uid: event.uid,
          rowVersion: event.rowVersion,
          title: event.title,
          description: event.description,
          startDateTime: dayjs(event.startDateTime),
          endDateTime: dayjs(event.endDateTime),
          company: { display: event.companyName, value: event.companyUid },
          departments: (event.departments ?? []).map((d) => ({
            display: d.departmentName,
            value: d.departmentUid,
          })),
          type: { display: event.typeName, value: event.typeUid },
          role: selectedEvent.dataItem!.role,
          originType: event.originType,
        });
        setEventOpen(true);
      })
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load the event."),
      );
  }

  function onEventFinish(): void {
    setEventOpen(false);
    calendar.reload();
    props.onEventClose?.();
  }

  function onEventCancel(): void {
    setEventOpen(false);
    props.onEventClose?.();
  }

  return (
    <>
      <PlatformCalendar<MyEvent>
        events={calendar.events}
        date={calendar.date}
        view={calendar.view}
        onNavigate={calendar.onNavigate}
        onView={calendar.onView}
        selectable
        onSelectSlot={onEventAdd}
        onSelectEvent={onEventSelect}
        agendaDescription={(item) => {
          // One line in the agenda: collapse line breaks, cap at 60 characters; full text on hover.
          const text = (item.dataItem?.eventDescription ?? "").replace(/\s+/g, " ").trim();
          return (
            <span title={text}>
              {_.truncate(text, { length: AGENDA_DESCRIPTION_LENGTH + 3, omission: "..." })}
            </span>
          );
        }}
        renderEvent={(item) => {
          const calendarEvent = item.dataItem!;
          const origin = originLabel(calendarEvent.eventOriginType);
          const departmentNames = (calendarEvent.eventDepartments ?? [])
            .map((d) => d.departmentName)
            .filter(Boolean)
            .join(", ");
          const meta = [calendarEvent.eventCompanyName, departmentNames]
            .filter((f) => !!f)
            .join(" · ");
          const timeRange = `${dayjs(calendarEvent.eventStartDate).format("HH:mm")} – ${dayjs(calendarEvent.eventEndDate).format("HH:mm")}`;
          return (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-medium leading-tight">{calendarEvent.eventTitle}</span>
              <span className="truncate text-[11px] leading-tight opacity-70">{timeRange}</span>
              {origin && (
                <Tag
                  color="purple"
                  bordered={false}
                  className="mr-0 self-start rounded px-1 py-0 text-[10px] leading-tight"
                >
                  {origin}
                </Tag>
              )}
              {meta && <span className="truncate text-[11px] leading-tight opacity-80">{meta}</span>}
            </span>
          );
        }}
      />
      <UserCalendarEventDialog
        open={eventOpen}
        mode={eventType}
        initialValues={selectedItem}
        onMode={onEventMode}
        onFinish={onEventFinish}
        onCancel={onEventCancel}
      />
    </>
  );
}

export default MyCalendarView;
